import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash, Sparkle, ArrowRight, Users } from "@phosphor-icons/react";

const ROLES = ["admin", "accountant", "viewer"];

export default function TeamPage() {
  const { activeCompany, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "accountant" });

  // Upgrade modal state — triggered when invite 402s on seat cap
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null); // { email, role, message }
  const [upgrading, setUpgrading] = useState(false);

  const load = async () => {
    if (!activeCompany) return;
    const [membersRes, subRes] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/members`),
      api.get(`/subscription/me`).catch(() => ({ data: null })),
    ]);
    setMembers(membersRes.data);
    setSub(subRes.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const isSeatCap402 = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    return status === 402 && /seat limit/i.test(String(detail));
  };

  const invite = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    try {
      await api.post(`/companies/${activeCompany.id}/members`, payload);
      toast.success("Member added");
      setOpen(false);
      setForm({ email: "", role: "accountant" });
      load();
    } catch (e2) {
      if (isSeatCap402(e2)) {
        setPendingInvite({
          email: payload.email,
          role: payload.role,
          message: e2.response.data.detail,
        });
        setOpen(false);
        setUpgradeOpen(true);
        return;
      }
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  // "Confirm & invite" from the upgrade modal
  const confirmUpgradeAndInvite = async () => {
    if (!pendingInvite) return;
    setUpgrading(true);
    try {
      // Active Stripe sub → add +1 seat live on Stripe
      if (sub?.plan_status === "active" && sub?.stripe_subscription_id) {
        const newSeats = (sub.seat_addons || 0) + 1;
        const seatRes = await api.post(`/subscription/seats`, { seat_addons: newSeats });
        if (!seatRes.data?.ok) throw new Error("Failed to add seat");
        // Retry invite
        await api.post(`/companies/${activeCompany.id}/members`, {
          email: pendingInvite.email,
          role: pendingInvite.role,
        });
        toast.success(`Added 1 seat and invited ${pendingInvite.email}`);
        setUpgradeOpen(false);
        setPendingInvite(null);
        setForm({ email: "", role: "accountant" });
        load();
      } else {
        // No active sub — route them to subscription page to pick a plan
        toast.info("You need an active subscription to add extra seats. Redirecting…");
        setUpgradeOpen(false);
        navigate("/subscription");
      }
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUpgrading(false);
    }
  };

  const updateRole = async (mid, role) => {
    try {
      await api.patch(`/companies/${activeCompany.id}/members/${mid}`, { role });
      toast.success("Role updated");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (mid) => {
    if (!window.confirm("Remove member?")) return;
    await api.delete(`/companies/${activeCompany.id}/members/${mid}`);
    toast.success("Removed");
    load();
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const seatsUsed = sub?.seats_used ?? "—";
  const seatsLimit = sub?.seats_limit ?? "—";
  const isGrandfathered = sub?.plan_status === "grandfathered";
  const seatUnitPrice = sub?.plan_interval === "year" ? "£250 / year" : "£25 / month";

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Team & Permissions"
        description={`Members of ${activeCompany.name}. Users must register first, then be invited by email.`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="invite-member-btn"
                className="rounded-md h-9 gap-2"
              >
                <UserPlus size={16} weight="bold" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-lg">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Add Member</DialogTitle>
                {sub && !isGrandfathered && (
                  <DialogDescription className="flex items-center gap-1 text-xs">
                    <Users size={14} /> Using {seatsUsed} of {seatsLimit} seats
                  </DialogDescription>
                )}
              </DialogHeader>
              <form onSubmit={invite} className="space-y-3">
                <div>
                  <Label className="text-xs font-medium">Email</Label>
                  <Input
                    type="email"
                    data-testid="invite-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="rounded-md"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="invite-role" className="rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="invite-submit"
                    className="rounded-md"
                  >
                    Add
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Upgrade / Add-a-seat modal */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="rounded-lg" data-testid="seat-upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-bold">
              <Sparkle size={22} weight="fill" className="text-primary" />
              Add one more seat
            </DialogTitle>
            <DialogDescription>
              You've reached your plan's user limit. Add 1 seat for{" "}
              <b>{seatUnitPrice}</b> and invite <b>{pendingInvite?.email}</b> right now.
            </DialogDescription>
          </DialogHeader>

          <div className="qb-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Current usage</div>
                <div className="text-lg font-semibold num">{seatsUsed} / {seatsLimit} seats</div>
              </div>
              <ArrowRight size={20} className="text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">After upgrade</div>
                <div className="text-lg font-semibold num text-primary">
                  {seatsUsed} / {Number(seatsLimit) + 1} seats
                </div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {sub?.plan_status === "active"
                ? `Prorated ${seatUnitPrice.toLowerCase()} added to your ${sub?.plan_interval}ly Stripe invoice.`
                : "You'll be redirected to Subscription to activate a plan first."}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setUpgradeOpen(false); setPendingInvite(null); }}
              data-testid="seat-upgrade-cancel"
              className="rounded-md"
            >
              Not now
            </Button>
            <Button
              onClick={confirmUpgradeAndInvite}
              disabled={upgrading}
              data-testid="seat-upgrade-confirm"
              className="rounded-md gap-2"
            >
              {upgrading ? "Adding seat…"
                : sub?.plan_status === "active" ? "Confirm & invite"
                : "Go to Subscription"}
              {!upgrading && <ArrowRight size={14} weight="bold" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PageBody>
        {sub && !isGrandfathered && (
          <div className="qb-card p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                <Users size={20} weight="fill" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">User seats</div>
                <div className="text-lg font-semibold num">
                  {seatsUsed} <span className="text-muted-foreground font-normal">/ {seatsLimit}</span>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/subscription")} className="rounded-md" data-testid="team-manage-seats">
              Manage seats
            </Button>
          </div>
        )}

        <div className="qb-card overflow-hidden">
          <table className="w-full qb-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="text-right pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="font-medium">{m.user?.name}</td>
                  <td className="text-muted-foreground">{m.user?.email}</td>
                  <td>
                    <Select value={m.role} onValueChange={(v) => updateRole(m.id, v)}>
                      <SelectTrigger data-testid={`member-role-${m.id}`} className="rounded-md h-8 w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} className="capitalize">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-right pr-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(m.id)}
                      className="rounded-md h-8 text-destructive"
                      data-testid={`member-remove-${m.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-muted-foreground">
                    No members.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
